'use client'

import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { Card } from '@/services/cards.service'
import type {
  CitizenWallet,
  WalletActivity,
  WalletCard,
  WalletStatistics,
} from '@/services/wallet.service'
import type { RootState } from '@/store'

interface WalletState {
  // Data
  wallet: CitizenWallet | null
  walletCards: Card[]
  favoriteCards: Card[]
  primaryCard: Card | null
  activities: WalletActivity[]
  statistics: WalletStatistics | null
  notifications: Array<{
    id: string
    type: string
    title: string
    message: string
    read: boolean
    created_at: string
  }>

  // UI State
  isLoading: boolean
  isSaving: boolean
  error: string | null

  // Security State
  pinVerified: boolean
  biometricVerified: boolean
  lastVerificationTime: string | null

  // Filters for activities
  activityFilters: {
    startDate: string | null
    endDate: string | null
    activityType: string | null
    page: number
    limit: number
  }

  // Modal States
  modals: {
    setPIN: boolean
    changePIN: boolean
    verifyPIN: boolean
    enableBiometric: boolean
    addCard: boolean
    viewActivity: boolean
    exportWallet: boolean
  }
}

const initialState: WalletState = {
  wallet: null,
  walletCards: [],
  favoriteCards: [],
  primaryCard: null,
  activities: [],
  statistics: null,
  notifications: [],
  isLoading: false,
  isSaving: false,
  error: null,
  pinVerified: false,
  biometricVerified: false,
  lastVerificationTime: null,
  activityFilters: {
    startDate: null,
    endDate: null,
    activityType: null,
    page: 1,
    limit: 10,
  },
  modals: {
    setPIN: false,
    changePIN: false,
    verifyPIN: false,
    enableBiometric: false,
    addCard: false,
    viewActivity: false,
    exportWallet: false,
  },
}

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    // Wallet Data Actions
    setWallet: (state, action: PayloadAction<CitizenWallet>) => {
      state.wallet = action.payload
      state.error = null
    },

    clearWallet: (state) => {
      state.wallet = null
      state.walletCards = []
      state.favoriteCards = []
      state.primaryCard = null
      state.activities = []
      state.statistics = null
    },

    setWalletCards: (state, action: PayloadAction<Card[]>) => {
      state.walletCards = action.payload
    },

    setFavoriteCards: (state, action: PayloadAction<Card[]>) => {
      state.favoriteCards = action.payload
    },

    setPrimaryCard: (state, action: PayloadAction<Card | null>) => {
      state.primaryCard = action.payload
    },

    setActivities: (state, action: PayloadAction<WalletActivity[]>) => {
      state.activities = action.payload
    },

    addActivity: (state, action: PayloadAction<WalletActivity>) => {
      state.activities.unshift(action.payload)
    },

    setStatistics: (state, action: PayloadAction<WalletStatistics>) => {
      state.statistics = action.payload
    },

    setNotifications: (state, action: PayloadAction<WalletState['notifications']>) => {
      state.notifications = action.payload
    },

    markNotificationRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },

    // Card Management Actions
    addCardToWallet: (state, action: PayloadAction<Card>) => {
      state.walletCards.push(action.payload)
      if (state.statistics) {
        state.statistics.total_cards += 1
      }
    },

    removeCardFromWallet: (state, action: PayloadAction<string>) => {
      state.walletCards = state.walletCards.filter((card) => card.id !== action.payload)
      state.favoriteCards = state.favoriteCards.filter((card) => card.id !== action.payload)
      if (state.primaryCard?.id === action.payload) {
        state.primaryCard = null
      }
      if (state.statistics) {
        state.statistics.total_cards -= 1
      }
    },

    toggleFavorite: (state, action: PayloadAction<string>) => {
      const card = state.walletCards.find((c) => c.id === action.payload)
      if (card) {
        const isFavorite = state.favoriteCards.some((c) => c.id === action.payload)
        if (isFavorite) {
          state.favoriteCards = state.favoriteCards.filter((c) => c.id !== action.payload)
          if (state.statistics) {
            state.statistics.favorite_cards -= 1
          }
        } else {
          state.favoriteCards.push(card)
          if (state.statistics) {
            state.statistics.favorite_cards += 1
          }
        }
      }
    },

    // Security Actions
    setPINVerified: (state, action: PayloadAction<boolean>) => {
      state.pinVerified = action.payload
      if (action.payload) {
        state.lastVerificationTime = new Date().toISOString()
      }
    },

    setBiometricVerified: (state, action: PayloadAction<boolean>) => {
      state.biometricVerified = action.payload
      if (action.payload) {
        state.lastVerificationTime = new Date().toISOString()
      }
    },

    clearVerification: (state) => {
      state.pinVerified = false
      state.biometricVerified = false
      state.lastVerificationTime = null
    },

    // Activity Filter Actions
    setActivityFilters: (state, action: PayloadAction<Partial<WalletState['activityFilters']>>) => {
      state.activityFilters = { ...state.activityFilters, ...action.payload }
    },

    clearActivityFilters: (state) => {
      state.activityFilters = initialState.activityFilters
    },

    // UI State Actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    clearError: (state) => {
      state.error = null
    },

    // Modal Actions
    setModal: (
      state,
      action: PayloadAction<{ modal: keyof WalletState['modals']; open: boolean }>
    ) => {
      state.modals[action.payload.modal] = action.payload.open
    },

    openSetPINModal: (state) => {
      state.modals.setPIN = true
    },

    openChangePINModal: (state) => {
      state.modals.changePIN = true
    },

    openVerifyPINModal: (state) => {
      state.modals.verifyPIN = true
    },

    openEnableBiometricModal: (state) => {
      state.modals.enableBiometric = true
    },

    openAddCardModal: (state) => {
      state.modals.addCard = true
    },

    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key as keyof WalletState['modals']] = false
      })
    },

    // Reset State
    resetWalletState: () => initialState,
  },
})

// Export actions
export const {
  setWallet,
  clearWallet,
  setWalletCards,
  setFavoriteCards,
  setPrimaryCard,
  setActivities,
  addActivity,
  setStatistics,
  setNotifications,
  markNotificationRead,
  addCardToWallet,
  removeCardFromWallet,
  toggleFavorite,
  setPINVerified,
  setBiometricVerified,
  clearVerification,
  setActivityFilters,
  clearActivityFilters,
  setLoading,
  setSaving,
  setError,
  clearError,
  setModal,
  openSetPINModal,
  openChangePINModal,
  openVerifyPINModal,
  openEnableBiometricModal,
  openAddCardModal,
  closeAllModals,
  resetWalletState,
} = walletSlice.actions

// Selectors
export const selectWalletState = (state: RootState) => state.wallet
export const selectWallet = (state: RootState) => state.wallet.wallet
export const selectWalletCards = (state: RootState) => state.wallet.walletCards
export const selectFavoriteCards = (state: RootState) => state.wallet.favoriteCards
export const selectPrimaryCard = (state: RootState) => state.wallet.primaryCard
export const selectWalletActivities = (state: RootState) => state.wallet.activities
export const selectWalletStatistics = (state: RootState) => state.wallet.statistics
export const selectWalletNotifications = (state: RootState) => state.wallet.notifications
export const selectWalletLoading = (state: RootState) => state.wallet.isLoading
export const selectWalletError = (state: RootState) => state.wallet.error
export const selectPINVerified = (state: RootState) => state.wallet.pinVerified
export const selectBiometricVerified = (state: RootState) => state.wallet.biometricVerified
export const selectWalletModals = (state: RootState) => state.wallet.modals

// Export reducer
export default walletSlice.reducer
